import React, { useState, useEffect } from 'react';
import type { Match } from '../hooks/useMatches';
import { X } from 'lucide-react';

interface Props {
  match: Match | null;
  isNew?: boolean;
  onSave: (data: Partial<Match>) => Promise<void>;
  onClose: () => void;
}

export function MatchModal({ match, isNew, onSave, onClose }: Props) {
  const [form, setForm] = useState({
    home_team: '',
    away_team: '',
    competition: '',
    channel: '',
    match_date: new Date().toISOString().split('T')[0],
    match_time: '21:00',
    sport: 'football',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (match) {
      setForm({
        home_team: match.home_team || '',
        away_team: match.away_team || '',
        competition: match.competition || '',
        channel: match.channel || '',
        match_date: match.match_date || '',
        match_time: match.match_time || '21:00',
        sport: match.sport || 'football',
      });
    }
  }, [match]);

  const handleSave = async () => {
    if (!form.home_team || !form.away_team || !form.match_date) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        channel: form.channel || null,
      });
      onClose();
    } catch {
      // error handled upstream
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel slide-in">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--gold)' }}>
            {isNew ? 'Ajouter un match' : 'Modifier le match'}
          </h2>
          <button onClick={onClose} className="btn-icon"><X size={18} /></button>
        </div>

        <div className="form-grid-2">
          <div className="form-field">
            <label>Équipe domicile</label>
            <input value={form.home_team} onChange={e => setForm({ ...form, home_team: e.target.value })} placeholder="Ex: PSG" />
          </div>
          <div className="form-field">
            <label>Équipe extérieur</label>
            <input value={form.away_team} onChange={e => setForm({ ...form, away_team: e.target.value })} placeholder="Ex: Arsenal" />
          </div>
        </div>

        <div className="form-grid-2">
          <div className="form-field">
            <label>Compétition</label>
            <input value={form.competition} onChange={e => setForm({ ...form, competition: e.target.value })} placeholder="Ex: Champions League" />
          </div>
          <div className="form-field">
            <label>Chaîne TV</label>
            <input value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} placeholder="Ex: Canal+" />
          </div>
        </div>

        <div className="form-grid-3">
          <div className="form-field">
            <label>Date</label>
            <input type="date" value={form.match_date} onChange={e => setForm({ ...form, match_date: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Heure</label>
            <input type="time" value={form.match_time} onChange={e => setForm({ ...form, match_time: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Sport</label>
            <select value={form.sport} onChange={e => setForm({ ...form, sport: e.target.value })}>
              <option value="football">Football</option>
              <option value="rugby">Rugby</option>
              <option value="basketball">Basketball</option>
            </select>
          </div>
        </div>

        {match && (
          <p style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '12px', fontStyle: 'italic' }}>
            ID: {match.id.substring(0, 8)}...
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
          <button className="btn btn-outline" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sauvegarde...' : isNew ? 'Ajouter' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}
